import React from 'react';
import PropTypes from 'prop-types';
import {
  DiscountsSummaryCard,
  DiscountsSummaryDelta,
  DiscountsSummaryGrid,
  DiscountsSummaryInfo,
  DiscountsSummaryLabel,
  DiscountsSummaryValue
} from 'routes/App/discountsV2/DiscountsList/DiscountsToolbar/styles';

const PLACEHOLDER = '—';
const MIN_PERCENT_TO_SHOW = 1;

const formatCurrency = (value) =>
  Number.isFinite(value)
    ? value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      })
    : PLACEHOLDER;

const formatPercent = (value) =>
  Number.isFinite(value) ? String(parseFloat(Number(value).toFixed(1))) : null;

const shouldShowPercent = (value) =>
  Number.isFinite(value) && value > MIN_PERCENT_TO_SHOW;

const safeDivide = (numerator, denominator) =>
  Number.isFinite(numerator) && Number.isFinite(denominator) && denominator > 0
    ? numerator / denominator
    : null;

const DiscountsToolbar = ({summary, statistics}) => {
  const ordersCount = statistics?.ordersCount ?? null;
  const totalDiscountAmount = statistics?.totalDiscountAmount ?? null;
  const totalDiscountedSubtotal = statistics?.totalDiscountedSubtotal ?? null;
  const discountedOrdersPercent = statistics?.discountedOrdersPercent;
  const discountedAvgOrderValueLiftPercent =
    statistics?.discountedAvgOrderValueLiftPercent;

  const avgDiscountOrderValue = safeDivide(
    totalDiscountedSubtotal,
    ordersCount
  );
  const avgDiscountPerOrder = safeDivide(totalDiscountAmount, ordersCount);

  return (
    <DiscountsSummaryGrid>
      <DiscountsSummaryCard>
        <DiscountsSummaryValue>{summary.active}</DiscountsSummaryValue>
        <DiscountsSummaryInfo>
          <DiscountsSummaryLabel>Active Discounts</DiscountsSummaryLabel>
        </DiscountsSummaryInfo>
      </DiscountsSummaryCard>
      <DiscountsSummaryCard>
        <DiscountsSummaryValue>
          {ordersCount === null ? PLACEHOLDER : ordersCount}
        </DiscountsSummaryValue>
        <DiscountsSummaryInfo>
          <DiscountsSummaryLabel>
            Discount orders (last 30 days)
          </DiscountsSummaryLabel>
          {shouldShowPercent(discountedOrdersPercent) && (
            <DiscountsSummaryDelta>
              {formatPercent(discountedOrdersPercent)}% of orders
            </DiscountsSummaryDelta>
          )}
        </DiscountsSummaryInfo>
      </DiscountsSummaryCard>
      <DiscountsSummaryCard>
        <DiscountsSummaryValue>
          {avgDiscountOrderValue === null
            ? PLACEHOLDER
            : formatCurrency(avgDiscountOrderValue)}
        </DiscountsSummaryValue>
        <DiscountsSummaryInfo>
          <DiscountsSummaryLabel>
            Avg order value (last 30 days)
          </DiscountsSummaryLabel>
          {shouldShowPercent(discountedAvgOrderValueLiftPercent) && (
            <DiscountsSummaryDelta>
              {`+${formatPercent(
                discountedAvgOrderValueLiftPercent
              )}% vs non-discount orders`}
            </DiscountsSummaryDelta>
          )}
        </DiscountsSummaryInfo>
      </DiscountsSummaryCard>
      <DiscountsSummaryCard>
        <DiscountsSummaryValue>
          {avgDiscountPerOrder === null
            ? PLACEHOLDER
            : formatCurrency(avgDiscountPerOrder)}
        </DiscountsSummaryValue>
        <DiscountsSummaryInfo>
          <DiscountsSummaryLabel>
            Avg discount per order (last 30 days)
          </DiscountsSummaryLabel>
        </DiscountsSummaryInfo>
      </DiscountsSummaryCard>
    </DiscountsSummaryGrid>
  );
};

DiscountsToolbar.propTypes = {
  summary: PropTypes.shape({
    active: PropTypes.number
  }).isRequired,
  statistics: PropTypes.shape({
    ordersCount: PropTypes.number,
    totalDiscountAmount: PropTypes.number,
    totalDiscountedSubtotal: PropTypes.number,
    discountedOrdersPercent: PropTypes.number,
    discountedAvgOrderValueLiftPercent: PropTypes.number
  })
};

DiscountsToolbar.defaultProps = {
  statistics: null
};

export default DiscountsToolbar;
